import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { PAY_INTERVAL_VALUE, PayIntervalValue } from '../../model';
import {
  TICKET_TYPE,
  TICKET_TYPE_VALUES,
} from '#/components/ui/tickets/config';

const TicketSearchBarParams = {
  FUZZY_SEARCH_TEXT: 'q',
  INTERVAL: 'interval',
  TYPE: 'type',
} as const;

type TTicketSearchBarParams = typeof TicketSearchBarParams;

type SearchParams = {
  [TicketSearchBarParams.INTERVAL]: PayIntervalValue[];
  [TicketSearchBarParams.FUZZY_SEARCH_TEXT]: string | undefined;
  [TicketSearchBarParams.TYPE]: TICKET_TYPE[];
};

type SearchParamsKeys =
  (typeof TicketSearchBarParams)[keyof typeof TicketSearchBarParams];

const intervalSchema = z.enum(PAY_INTERVAL_VALUE).array();
const serviceTypeSchema = z.enum(TICKET_TYPE_VALUES).array();

export type DefaultOptionsParams = {
  interval?: SearchParams[TTicketSearchBarParams['INTERVAL']];
  fuzzySearchText?: SearchParams[TTicketSearchBarParams['FUZZY_SEARCH_TEXT']];
  type?: SearchParams[TTicketSearchBarParams['TYPE']];
};

export function useEnrollmentSearchBarParams(
  defaultOptions?: DefaultOptionsParams,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawInterval = searchParams.getAll(TicketSearchBarParams.INTERVAL);
  const rawType = searchParams.getAll(TicketSearchBarParams.TYPE);

  const fuzzySearchText =
    searchParams.get(TicketSearchBarParams.FUZZY_SEARCH_TEXT) ?? undefined;
  const interval = intervalSchema.catch([]).parse(rawInterval);
  const type = serviceTypeSchema.catch([]).parse(rawType);

  useEffect(() => {
    const defaultInit = defaultOptions
      ? (() => {
          const sp = new URLSearchParams();
          if (defaultOptions?.fuzzySearchText)
            sp.set(
              TicketSearchBarParams.FUZZY_SEARCH_TEXT,
              defaultOptions.fuzzySearchText,
            );
          if (defaultOptions?.interval) {
            for (const s of defaultOptions.interval) {
              sp.append(TicketSearchBarParams.INTERVAL, s);
            }
          }
          if (defaultOptions?.type) {
            for (const s of defaultOptions.type) {
              sp.append(TicketSearchBarParams.TYPE, s);
            }
          }
          return sp;
        })()
      : undefined;

    if (defaultInit) {
      setSearchParams(defaultInit);
    }
    // only apply first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSearch = <T extends SearchParamsKeys>(
    key: T,
    value: SearchParams[T],
  ) => {
    setSearchParams((prev) => {
      if (value == null || value === '') {
        prev.delete(key);
      } else if (Array.isArray(value)) {
        prev.delete(key);
        for (const v of value) {
          prev.append(key, v);
        }
      } else {
        prev.set(key, value);
      }
      return prev;
    });
  };

  const createSearchSetter =
    <T extends SearchParamsKeys>(key: T) =>
    (value: SearchParams[T]) =>
      setSearch(key, value);

  const setInterval = createSearchSetter(TicketSearchBarParams.INTERVAL);
  const setFuzzySearchText = createSearchSetter(
    TicketSearchBarParams.FUZZY_SEARCH_TEXT,
  );
  const setType = createSearchSetter(TicketSearchBarParams.TYPE);

  const reset = () => {
    setSearchParams((prev) => {
      prev.delete(TicketSearchBarParams.INTERVAL);
      prev.delete(TicketSearchBarParams.FUZZY_SEARCH_TEXT);
      prev.delete(TicketSearchBarParams.TYPE);
      return prev;
    });
  };

  return {
    params: { interval, fuzzySearchText, type },
    setInterval,
    setFuzzySearchText,
    setType,
    setSearchParams,
    reset,
  };
}
